// Shared shaping for the `conversions-by-journey-step` chart, consumed by the
// Monitor page and the Client Dashboard. The backend emits one row per
// (journey step, dimension) cell (MPN-1170); this groups the cells back under
// their step so a surface can show the step-level rollup alongside the
// dimensioned breakdown (MPN-1171).

export interface ConversionByStepRow {
  journey_step_id?: number | null;
  journey_step_dimension_id?: number | null;
  step_name?: string | null;
  journey_step_dimension_name?: string | null;
  conversions?: number | string;
}

export interface ConversionStepCell {
  dimension_id: number | null;
  dimension_name: string | null;
  conversions: number;
}

export interface ConversionStepGroup {
  journey_step_id: number | null;
  step_name: string;
  total: number;                 // step-level rollup = sum of its cells
  cells: ConversionStepCell[];   // the dimensioned breakdown (one per cell)
  dimensioned: boolean;          // true when the step is split across dimensions
}

/**
 * Group flat (step, dimension) rows into per-step groups, preserving the
 * backend's incoming order (sorted by step sequence then name). A step is
 * `dimensioned` when it has more than one cell or any cell carries a dimension
 * name — such steps get a subtotal header + a row per cell; undimensioned
 * single-cell steps render as a single plain row.
 */
export function groupConversionsByStep(rows: ConversionByStepRow[]): ConversionStepGroup[] {
  const groups: ConversionStepGroup[] = [];
  const byKey = new Map<string, ConversionStepGroup>();

  for (const row of rows ?? []) {
    const stepId = row.journey_step_id ?? null;
    const stepName = row.step_name ?? "Unknown";
    // Fall back to name when there is no id so distinct un-idd steps don't merge.
    const key = stepId !== null ? `id:${stepId}` : `name:${stepName}`;

    let group = byKey.get(key);
    if (!group) {
      group = { journey_step_id: stepId, step_name: stepName, total: 0, cells: [], dimensioned: false };
      byKey.set(key, group);
      groups.push(group);
    }

    const conversions = Number(row.conversions ?? 0);
    const dimensionName = row.journey_step_dimension_name ?? null;
    group.cells.push({
      dimension_id: row.journey_step_dimension_id ?? null,
      dimension_name: dimensionName,
      conversions,
    });
    group.total += conversions;
    if (dimensionName !== null) group.dimensioned = true;
  }

  for (const group of groups) {
    if (group.cells.length > 1) group.dimensioned = true;
  }

  return groups;
}
